import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@pantry/ui';
import { useRemoveMember, type MemberWithUser } from '@pantry/api-client';
import { UserMinusIcon } from '@heroicons/react/24/outline';

interface RemoveMemberDialogProps {
  householdId: string;
  member: MemberWithUser;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RemoveMemberDialog({
  householdId,
  member,
  open,
  onOpenChange,
}: RemoveMemberDialogProps) {
  const { t } = useLingui();
  const [error, setError] = useState<string | null>(null);

  const removeMember = useRemoveMember(householdId);

  const handleRemove = async () => {
    setError(null);

    try {
      await removeMember.mutateAsync(member.userId);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to remove member`;
      setError(message);
    }
  };

  const memberName = member.user.name || member.user.email;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
              <UserMinusIcon className="h-5 w-5 text-destructive" />
            </div>
            <Trans>Remove Member</Trans>
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-2">
            <Trans>
              Are you sure you want to remove{' '}
              <span className="font-medium text-foreground">{memberName}</span> from this household?
              They will lose access to all items and data in this household.
            </Trans>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
            {error}
          </div>
        )}
        <AlertDialogFooter className="gap-2 sm:gap-0">
          <AlertDialogCancel disabled={removeMember.isPending} className="rounded-xl">
            <Trans>Cancel</Trans>
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleRemove}
            disabled={removeMember.isPending}
            className="rounded-xl bg-destructive text-white hover:bg-destructive/90"
          >
            {removeMember.isPending ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <Trans>Removing...</Trans>
              </div>
            ) : (
              <Trans>Remove Member</Trans>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
